import { getAuth } from 'firebase/auth';
import config from 'config';
import axios from 'axios';
import {
  setIcpHistory,
  setIcpPagination,
  setLoadingAnswer,
  setIcpHistoryResult,
  createIcpRequest,
  createIcpSuccess,
  createIcpFailure
} from 'store/reducers/actions';
import { dispatch as storeDispatch } from 'store/index';

const auth = getAuth();
const apiUrl = config.apiUrl;

export const getIcpSegments = async (websiteUrl) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  try {
    const token = await user.getIdToken();

    const response = await axios.post(
      `${apiUrl}/lpcompare/icp_creation_flow_get_segments/`,
      { url: websiteUrl },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
      }
    );

    if (response.status !== 200) {
      throw new Error('Failed to fetch ICP segments.');
    }

    // Start polling with the task_id
    return pollIcpSegments(response.data.task_id);
  } catch (error) {
    console.error('Error fetching ICP segments:', error);
    throw error;
  }
};

const pollIcpSegments = async (taskId) => {
  let continuePolling = true;
  let retryCount = 0;
  const maxRetries = 40;
  const pollingInterval = 2000; // 5 seconds

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  while (continuePolling && retryCount < maxRetries) {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(`${apiUrl}/lpcompare/check_icp_creation_flow_get_segments/${taskId}/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
      });

      if (!response.data.processing_status) {
        // Task completed
        return {
          segments: response.data.segments || [],
          core_segmentation_model: response.data.core_segmentation_model,
          task_id: taskId,
          websiteUrl: response.data.url
        };
      }

      // Wait before next polling attempt
      await new Promise((resolve) => setTimeout(resolve, pollingInterval));
      retryCount++;
    } catch (error) {
      console.error('Error polling ICP segments:', error);
      throw error;
    }
  }

  throw new Error('Polling timeout: ICP segment analysis took too long');
};

/**
 * Create ICP for a specific segment
 * @param {string} taskId - The task ID for the ICP creation
 * @param {string} segmentId - The segment ID to create ICP for
 * @param {boolean} useRedux - Whether to automatically dispatch Redux actions (default: true)
 * @returns {Promise<Object>} The ICP creation result
 */
export const createIcp = async (taskId, segmentId, useRedux = true) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Use Redux if enabled
  const dispatchFunction = useRedux ? storeDispatch : null;

  // Dispatch request action if Redux is enabled
  if (dispatchFunction) {
    dispatchFunction(createIcpRequest());
  }

  try {
    const token = await user.getIdToken();

    const response = await axios.post(
      `${apiUrl}/lpcompare/create-icp/`,
      {
        task_id: taskId,
        segment_id: segmentId
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
      }
    );

    if (response.status !== 200) {
      throw new Error('Failed to create ICP.');
    }

    // Use icp_task_id instead of task_id from the response
    const result = await pollCreateIcp(response.data.icp_task_id, segmentId);

    // Dispatch success action if Redux is enabled
    if (dispatchFunction) {
      dispatchFunction(
        createIcpSuccess({
          segmentId: segmentId,
          icp: result.icp || result
        })
      );
    }

    return result;
  } catch (error) {
    console.error('Error creating ICP:', error);

    // Dispatch failure action if Redux is enabled
    if (dispatchFunction) {
      dispatchFunction(createIcpFailure(error.message || 'Failed to create ICP'));
    }

    throw error;
  }
};

const pollCreateIcp = async (taskId, segmentId) => {
  let continuePolling = true;
  let retryCount = 0;
  const maxRetries = 30;
  const pollingInterval = 3000; // 2 seconds

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  while (continuePolling && retryCount < maxRetries) {
    try {
      const token = await user.getIdToken();
      const response = await axios.get(`${apiUrl}/lpcompare/check_create_icp/${taskId}/${segmentId}/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
      });

      if (!response.data.processing_status) {
        // Task completed
        return response.data;
      }

      // Wait before next polling attempt
      await new Promise((resolve) => setTimeout(resolve, pollingInterval));
      retryCount++;
    } catch (error) {
      console.error('Error polling ICP creation:', error);
      throw error;
    }
  }

  throw new Error('Polling timeout: ICP creation took too long');
};

export const fetchICPHistory = (page = 1, limit = 10) => {
  return async (dispatch) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const token = await user.getIdToken();

      const response = await axios.get(`${apiUrl}/lpcompare/icp-history/`, {
        params: {
          page: page,
          page_size: limit
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
      });

      dispatch(setIcpHistory(response.data.results));
      dispatch(setIcpPagination(response.data.count));

      return response.data;
    } catch (error) {
      console.error('Error fetching ICP history:', error);
      throw error;
    }
  };
};

// Add new function to get specific ICP history results
export const getIcpHistoryResults = (taskId) => {
  return async (dispatch) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    dispatch(setLoadingAnswer(true));

    try {
      const token = await user.getIdToken();

      const response = await axios.get(`${apiUrl}/lpcompare/icp-history/${taskId}/`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        xsrfCookieName: 'XSRF-TOKEN',
        xsrfHeaderName: 'X-XSRF-TOKEN'
      });

      dispatch(setIcpHistoryResult(response.data));
      return response.data;
    } catch (error) {
      console.error('Error fetching ICP history results:', error);
      throw error;
    } finally {
      dispatch(setLoadingAnswer(false));
    }
  };
};
